import React, { createContext, useContext, useState, useCallback } from "react";

interface DisplayNamesHeaderContextType {
  dirty: boolean;
  saving: boolean;
  onSave: (() => Promise<void>) | null;
  onReset: (() => void) | null;
  setHeaderActions: (actions: {
    dirty: boolean;
    saving: boolean;
    onSave: () => Promise<void>;
    onReset: () => void;
  }) => void;
  clearHeaderActions: () => void;
}

const DisplayNamesHeaderContext = createContext<DisplayNamesHeaderContextType | undefined>(undefined);

export function DisplayNamesHeaderProvider({ children }: { children: React.ReactNode }) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [onSave, setOnSave] = useState<(() => Promise<void>) | null>(null);
  const [onReset, setOnReset] = useState<(() => void) | null>(null);

  const setHeaderActions = useCallback(
    (actions: { dirty: boolean; saving: boolean; onSave: () => Promise<void>; onReset: () => void }) => {
      setDirty(actions.dirty);
      setSaving(actions.saving);
      setOnSave(() => actions.onSave);
      setOnReset(() => actions.onReset);
    },
    [],
  );

  const clearHeaderActions = useCallback(() => {
    setDirty(false);
    setSaving(false);
    setOnSave(null);
    setOnReset(null);
  }, []);

  return (
    <DisplayNamesHeaderContext.Provider
      value={{
        dirty,
        saving,
        onSave,
        onReset,
        setHeaderActions,
        clearHeaderActions,
      }}
    >
      {children}
    </DisplayNamesHeaderContext.Provider>
  );
}

export function useDisplayNamesHeader() {
  const context = useContext(DisplayNamesHeaderContext);
  if (context === undefined) {
    throw new Error("useDisplayNamesHeader must be used within a DisplayNamesHeaderProvider");
  }
  return context;
}
